import { ShiftSlotViewModel, ComparisonDayViewModel } from '../utils/RequisitionDetailsViewModel';

export type ComparisonFlatRow = {
  id: string;
  dayId: string;
  dayLabel: string;
  isDayRequired: boolean;
  isDayMismatch?: boolean;
  isRowMismatch?: boolean;
  isFirstSubRow: boolean;
  subRowCount: number;
  desiredStart?: ShiftSlotViewModel;
  desiredEnd?: ShiftSlotViewModel;
  minHoursLabel?: string;
  minHoursMismatch?: boolean;
  candidateStart?: string;
  candidateEnd?: string;
};

// ─── Row Flattening ───────────────────────────────────────────────────────────

/**
 * Flatten the hierarchical ComparisonDayViewModel[] into a flat row list
 * suitable for react-table rendering.
 *
 * Each day may have multiple desired slots and multiple candidate slots.
 * The flattened output has N rows per day where N = max(desiredSlots, candidateSlots, 1).
 * Only the first sub-row carries the day label and min-hours data.
 */
export const flattenRows = (rows: ComparisonDayViewModel[]): ComparisonFlatRow[] => {
  const flat: ComparisonFlatRow[] = [];

  rows.forEach((day) => {
    const desiredSlots: Array<{ start?: ShiftSlotViewModel; end?: ShiftSlotViewModel }> = [
      {
        start: day.desiredSlot1Start,
        end: day.desiredSlot1End,
      },
    ];

    if (day.desiredSlot2Start !== undefined || day.desiredSlot2End !== undefined) {
      desiredSlots.push({
        start: day.desiredSlot2Start,
        end: day.desiredSlot2End,
      });
    }

    const rowCount = Math.max(desiredSlots.length, day.candidateSlots.length, 1);

    for (let i = 0; i < rowCount; i += 1) {
      flat.push({
        id: `${day.id}-${i}`,
        dayId: day.id,
        dayLabel: day.dayLabel,
        isDayRequired: day.isDayRequired,
        isDayMismatch: day.isDayMismatch,
        isRowMismatch: day.isRowMismatch,
        isFirstSubRow: i === 0,
        subRowCount: rowCount,

        desiredStart: desiredSlots[i]?.start,
        desiredEnd: desiredSlots[i]?.end,

        minHoursLabel: i === 0 ? day.minHoursLabel : undefined,
        minHoursMismatch: i === 0 ? day.minHoursMismatch : undefined,

        candidateStart: day.candidateSlots[i]?.startLabel,
        candidateEnd: day.candidateSlots[i]?.endLabel,
      });
    }
  });

  return flat;
};
